import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, Check, X, Loader2 } from 'lucide-react';

const ThumbnailCropper = ({ image, onCropComplete, onCancel, aspectRatio = 16/9, saving = false }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteHandler = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    // Return the crop position as percentages for CSS object-position
    const position = {
      x: crop.x,
      y: crop.y,
      zoom: zoom,
      // Convert to object-position percentages
      objectPosition: `${50 - crop.x}% ${50 - crop.y}%`
    };
    // Call the async handler - it will handle cleanup
    await onCropComplete(position);
    setIsSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="relative w-full h-64 bg-black rounded-lg overflow-hidden">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropCompleteHandler}
          objectFit="contain"
          showGrid={false}
          style={{
            containerStyle: {
              borderRadius: '0.5rem'
            }
          }}
        />
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <ZoomOut className="w-4 h-4 text-muted-foreground" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={(value) => setZoom(value[0])}
            className="flex-1"
          />
          <ZoomIn className="w-4 h-4 text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Drag to position, zoom to adjust
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} size="sm" disabled={isSaving || saving}>
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
        <Button onClick={handleSave} size="sm" disabled={isSaving || saving}>
          {(isSaving || saving) ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-1" />
              Apply
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ThumbnailCropper;
